import { COBClientDetail } from "creso-core/types";
import { useMemo } from "react";
import { cn } from "web-design-system";

const STEPS = [
  {
    key: "downloaded_count" as const,
    label: "Downloaded",
    full: "bg-cyan-500 border-cyan-500",
    partial: "bg-cyan-100 border-cyan-500",
  },
  {
    key: "in_transit_count" as const,
    label: "In Transit",
    full: "bg-amber-400 border-amber-400",
    partial: "bg-amber-100 border-amber-400",
  },
  {
    key: "under_processing_count" as const,
    label: "Processing",
    full: "bg-amber-400 border-amber-400",
    partial: "bg-amber-100 border-amber-400",
  },
  {
    key: "completed_count" as const,
    label: "Completed",
    full: "bg-green-500 border-green-500",
    partial: "bg-green-100 border-green-500",
  },
];

export function CobStatusProgress({ client }: { client: COBClientDetail }) {
  const steps = useMemo(() => {
    return [
      ...STEPS,
      ...(client.rejected_count > 0
        ? [
            {
              key: "rejected_count" as const,
              label: "Rejected",
              full: "bg-red-500",
              partial: "bg-red-100 border-red-500",
            },
          ]
        : []),
    ];
  }, [client.rejected_count]);

  const counts = steps.map((step) => (client[step.key] as number) || 0);

  // Index of the last step that has any folios (determines how far the progress goes)
  const lastActiveIdx = counts.reduce(
    (last, count, idx) => (count > 0 ? idx : last),
    -1
  );

  return (
    <div className="flex items-center pb-4">
      {steps.map((step, idx) => {
        const count = counts[idx];
        const isActive = count > 0;
        // Step was passed through (before the last active step, but currently has 0 folios)
        const isPassed = !isActive && lastActiveIdx >= 0 && idx < lastActiveIdx;
        // Line leading into this dot is solid once it falls within the active range
        const lineIsSolid = idx > 0 && idx <= lastActiveIdx;

        let dotClass: string;
        if (isActive) {
          dotClass = count === client.folio_count ? step.full : step.partial;
        } else if (isPassed) {
          dotClass = "bg-slate-400 border-slate-400";
        } else {
          dotClass = "bg-transparent border-slate-300";
        }

        return (
          <div key={step.key} className="flex items-center">
            {idx > 0 && (
              <div
                className={cn(
                  "h-px w-18 flex-shrink-0",
                  lineIsSolid
                    ? "bg-slate-400"
                    : "bg-transparent border-t border-dashed border-slate-300"
                )}
              />
            )}
            {/* Dot wrapper is only as wide as the dot itself; label is absolutely positioned */}
            <div className="relative flex-shrink-0">
              <div
                className={cn("w-2.5 h-2.5 rounded-full border", dotClass)}
              />
              {isActive && (
                <span className="absolute top-full left-1/2 -translate-x-1/2 pt-1.5 text-[10px] leading-none text-muted-foreground whitespace-nowrap pointer-events-none">
                  {count > 0 ? `${count} ` : ""}
                  {step.label}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
